import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductItemForm from './ProductItemForm';
import categoryAPI from '../../services/category';
import brandAPI from '../../services/brandapi';
import { Col, Row, Form } from 'react-bootstrap';
import { debounce } from 'lodash';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import productAPI from '../../services/productapi';
import { triggerDynamicAlerts } from '../../components/alert/sweetAlert';

const ProductForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    const productId = id ? parseInt(id) : null;
    // console.log(productId, 'productId');
    const { register, handleSubmit, setValue, control, watch, formState: { errors, isDirty }, } = useForm();
    const watchedProductName = watch("productName");
    const watchedDescription = watch("description");
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [product, setProduct] = useState(null);
    const [products, setProducts] = useState([]);
    const [productItems, setProductItems] = useState([]);
    // const [selectedRelatedProducts, setSelectedRelatedProducts] = useState([]);
    // const [searchQuery, setSearchQuery] = useState('');
    const [keyTags, setKeyTags] = useState([]);
    const [seoTitle, setSeoTitle] = useState('');
    const [seoKeywords, setSeoKeywords] = useState('');
    const [seoDescription, setSeoDescription] = useState('');
    const [hsn, setHsnSacCode] = useState('');
    const [slug, setSlug] = useState('');
    const [videoURL, setVideoURL] = useState('');
    const [availability, setAvailability] = useState(true);
    const [sudexo, setSudexo] = useState(false);
    const [weighable, setWeighable] = useState(false);
    const [stockFlag, setStockFlag] = useState(false);
    const [newProductID, setNewProductID] = useState(productId);
    // const [isProductSaved, setIsProductSaved] = useState(productId === null ? false : true);
    const [isProductSaved, setIsProductSaved] = useState(false);
    const [originalSlug, setOriginalSlug] = useState('');
    const [slugEditAllow, setSlugEditAllow] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleBack = async () => {
        // if user has changed something in form
        if (isDirty) {
            const result = await triggerDynamicAlerts(
                'Leave without saving?',
                'You have unsaved changes. If you leave now, your changes will be lost.',
                'warning',
                'Leave'
            );
            // console.log(result, "Assa");
            if (result.isConfirmed) {
                navigate('/admin/products');
            }
        } else {
            // directly go back if no change
            navigate('/admin/products');
        }
    };

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.altKey && e.key === 'ArrowLeft') {
                e.preventDefault();
                handleBack();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isDirty]);


    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await categoryAPI.getAllCategory(10000, 0);
                const flatCategories = (data?.data || []).filter(cat => cat.status === 1); // ✅ Only active categories

                // Step 1: Normalize and map categories by ID
                const categoryMap = {};
                flatCategories.forEach((cat) => {
                    const normalized = {
                        ...cat,
                        parentId: cat.parentID || cat.parent_id || 0,
                        children: [],
                    };
                    categoryMap[cat.categoryID] = normalized;
                });

                // Step 2: Build tree structure
                const categoryTree = [];
                Object.values(categoryMap).forEach((cat) => {
                    if (cat.parentId === 0) {
                        categoryTree.push(cat);
                    } else {
                        const parent = categoryMap[cat.parentId];
                        if (parent) {
                            parent.children.push(cat);
                        }
                    }
                });

                // Step 3: Flatten with fullPath for display
                const flatWithPath = [];
                const stack = categoryTree.map(cat => ({ node: cat, path: cat.categoryName }));

                while (stack.length > 0) {
                    const { node, path } = stack.pop();
                    flatWithPath.push({
                        ...node,
                        id: node.categoryID,
                        name: path, // e.g., "Main > Sub > SubSub"
                    });

                    if (node.children && node.children.length > 0) {
                        node.children.forEach(child => {
                            stack.push({
                                node: child,
                                path: `${path} > ${child.categoryName}`,
                            });
                        });
                    }
                }

                // Step 4: Set state
                setCategories(flatWithPath);
            } catch (error) {
                toast.error(`Failed to fetch categories: ${error.message || error}`);
            }
        };
        const fetchBrands = async () => {
            try {
                const data = await brandAPI.getAllBrands({ limit: 10000, page: 0 });
                const formattedData = data?.data.result.map((brands, index) => ({
                    ...brands,
                    id: brands.id,
                    name: brands.name,
                }));
                setBrands(formattedData);
            } catch (error) {
                toast.error(`Failed to fetch brands: ${error}`);
            }
        };

        fetchCategories();
        fetchBrands();


    }, [setValue]);



    // Now, use a separate useEffect to fetch the product data after categories and brands are set
    useEffect(() => {
        if (categories.length > 0 && brands.length > 0 && productId) {
            // Fetch product data only after categories and brands are available

            productAPI.getProductsData(productId).then((res) => {
                setProduct(res.data);
                setValue("productName", res.data.productName);
                setValue("productTamilName", res.data.productTamilName);
                setValue("slug", res.data.slug);
                setValue("categoryId", {
                    value: res.data.categoryId,
                    label: categories.find(cat => cat.categoryID === res.data.categoryId)?.categoryName || ''
                });

                setValue("brandId", {
                    value: res.data.brandId,
                    label: brands.find(brand => brand.id === res.data.brandId)?.name || ''
                });
                setValue("description", res.data.description);
                setKeyTags(res.data.keyTags.split(',') || []);

                const seo = JSON.parse(res.data.seo);
                const { og_image, title, desc, keyword, pageurl } = seo;

                const relatedProductsOptions = (res.data.relatedProductsDetails || [])
                    .map((product) => ({
                        value: product.id,
                        label: product.productName || ''
                    }));

                // setSelectedRelatedProducts(relatedProductsOptions);
                setValue("related_products", relatedProductsOptions);

                setSeoTitle(title || '');
                setSeoKeywords(keyword || '');
                setSeoDescription(desc || '');
                setHsnSacCode(res.data.hsn || '');
                setSlug(res.data.slug || '');
                setOriginalSlug(res.data.slug || '');
                setSlugEditAllow(false);
                // Set additional fields
                setAvailability(res.data.availability || true);
                setSudexo(res.data.sudexo || false);
                setWeighable(res.data.weighable || false);
                setStockFlag(res.data.stockFlag || false);
                setVideoURL(res.data.pro_video || '');
                if (res.data.items.length > 0) {
                    setProductItems(res.data.items);
                    setIsProductSaved(true);
                }
                setNewProductID(productId);
            }).catch((error) => {
                // console.log(error, 'error');
            });

        }
    }, [categories, brands, productId]);  // This effect depends on categories, brands, and productId


    useEffect(() => {
        if (watchedProductName) {
            const generatedTitle = `${watchedProductName} - Buy ${process.env.REACT_APP_WEB_NAME} at Best Price`;
            const generatedKeywords = watchedProductName.toLowerCase().split(" ").join(", ");
            const generatedDescription = `Buy ${watchedProductName} online. ${watchedDescription || ''}`.slice(0, 150);
            const generatedTags = watchedProductName.toLowerCase().split(" ").filter(Boolean);

            setSeoTitle(generatedTitle);
            setSeoKeywords(generatedKeywords);
            setSeoDescription(generatedDescription);

            // Only auto-set key tags if user hasn't typed anything
            // if (keyTags.length === 0) {
            setKeyTags(generatedTags);
            // }
        }
    }, [watchedProductName, watchedDescription]);

    const searchRelatedProducts = debounce((query) => {
        if (query.length > 2) {
            const start = Date.now(); // start time

            productAPI.getAllProducts({
                limit: 15,
                page: 0,
                filterModel: {
                    quickFilterValues: [query]
                }
            })
                .then(res => {
                    const end = Date.now(); // end time
                    // console.log(`[related_products API] Query: "${query}" | Time: ${end - start}ms`);
                    // console.log("API Result:", res.data);

                    const result = res?.data?.result || [];
                    const mappedProducts = result.map(product => ({
                        value: product.productId,
                        label: product.productName,
                    }));

                    // console.log("Mapped options:", mappedProducts); // check what's getting set
                    setProducts(mappedProducts);
                })
                .catch(err => toast.error(`Error fetching products: ${err}`));
        } else {
            setProducts([]);
        }
    }, 500);

    // const handleSearchChange = (newValue) => {
    //     setSearchQuery(newValue);
    //     searchRelatedProducts(newValue);
    // };

    const handleSearchChange = (inputText) => {
        // console.log("[handleSearchChange] input:", inputText);
        searchRelatedProducts(inputText);
    };

    const handleKeyTagChange = (e) => {
        if (e.key === 'Enter' && e.target.value.trim()) {
            e.preventDefault();
            setKeyTags([...keyTags, e.target.value.trim()]);
            e.target.value = '';
        }
    };

    const removeKeyTag = (index) => {
        setKeyTags(keyTags.filter((_, i) => i !== index));
    };

    const generateSlug = async (productName) => {
        let newSlug = productName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        try {
            const response = await productAPI.checkSlugExists(newSlug, productId);
            if (response.data.exists) {
                newSlug = `${newSlug}-${Math.floor(Math.random() * 1000)}`;
            }
            setSlug(newSlug);
            setValue("slug", newSlug);
        } catch (error) {
            toast.error(`Error checking slug existence: ${error} `);
        }
    };

    const handleProductNameBlur = (e) => {
        const productName = e.target.value;
        //  do not auto-generate slug when editing
        if (!productId && productName) {
            generateSlug(productName);
        }
    };

    const handleFormKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent form submission on "Enter"
        }
    };

    const handleSlugEdit = () => {
        setSlugEditAllow((prev) => {
            const next = !prev;
            if (!next && originalSlug) {
                setSlug(originalSlug);
                setValue("slug", originalSlug);
            }
            return next;
        });
    };

    const onSubmit = (data) => {
        setLoading(true);
        const relatedProductIds = (data.related_products || [])
            .map(p => p.value)
            .join(',');
        delete data.related_products;

        const updatedData = {
            ...data,
            brandId: data.brandId ? data.brandId.value : null,
            categoryId: data.categoryId ? data.categoryId.value : null,
            relatedProducts: relatedProductIds,
        };

        const formData = {
            ...updatedData,
            productId: newProductID || productId,
            keyTags: keyTags.length > 0 ? keyTags.join(',') : '',
            seoTitle,
            seoKeywords,
            seoDescription,
            hsn,
            slug,
            availability,
            sudexo,
            weighable,
            stockFlag
        };

        productAPI.updateProducts(formData)
            .then((productResponse) => {
                // console.log(productResponse, 'After save response');
                setNewProductID(productResponse.data.productId);
                setIsProductSaved(true);
                toast.success(productId ? "Product updated successfully!" : "Product added successfully!");
            })
            .catch(() => {
                toast.error("An error occurred while saving the product.");
            })
            .finally(() => {
                setLoading(false); // ⬅️ Stop loading regardless of success/failure
            });
    };

    // console.log('newProductID', newProductID);

    // useEffect(() => {
    //     console.log("[State] products in dropdown:", products);
    // }, [products]);

    return (

        <form onSubmit={handleSubmit(onSubmit)} className="container mt-5 mb-5" onKeyDown={handleFormKeyDown}>

            <ToastContainer />
            <div className="d-flex align-items-center justify-content-between">
                <h2 className='col-8'>{productId ? "Edit Product" : "Add New Product"}</h2>
                <button
                    type="button"
                    className="btn btn-outline-secondary  btn-sm back-btn"
                    onClick={handleBack}
                >
                    ‹ Back to Products
                </button>
            </div>
            <Row className="mt-4">
                <Col md="6">
                    <div className="mb-3">
                        <label className="form-label">Product Name</label>
                        <input
                            {...register("productName", {
                                required: "Product Name is required",
                                minLength: { value: 3, message: "Product Name must be at least 3 characters" }
                            })}
                            className="form-control"
                            type="text"
                            onBlur={handleProductNameBlur}
                            required
                        />
                        {errors.productName && <p className="text-danger">{errors.productName.message}</p>}
                    </div>

                    <div className="mb-3 ">
                        {productId && (
                            <span className="text-danger float-end" onClick={handleSlugEdit} style={{ cursor: 'pointer' }}>
                                {slugEditAllow ? 'Cancel Slug Edit' : 'Edit Slug'}
                            </span>
                        )}
                        {/* 
                        
                        <label className="form-label w-100">Product Slug
                            <span className="text-danger float-end" onClick={handleSlugEdit}> {slugEditAllow ? 'Remove Edit' : 'Allow Edit'}</span>
                        </label>
                        <input
                            {...register("slug", {
                                required: "Product Slug is required",
                                pattern: {
                                    value: /^[a-z0-9-]+$/,
                                    message: "Product Slug must contain only lowercase letters, numbers, and hyphens"
                                }
                            })}
                            value={slug}
                            className="form-control"
                            type="text"
                            readOnly={!slugEditAllow}
                            onChange={(e) => setSlug(e.target.value)}
                            onBlur={() => generateSlug(slug)}
                            required
                        /> */}
                        <input
                            {...register("slug", {
                                required: "Product Slug is required",
                                pattern: {
                                    value: /^[a-z0-9-]+$/,
                                    message: "Product Slug must contain only lowercase letters, numbers, and hyphens"
                                }
                            })}
                            value={slug}
                            className="form-control"
                            type="text"
                            readOnly={productId ? !slugEditAllow : false}     // ✅ create editable, edit locked until toggle
                            onChange={(e) => setSlug(e.target.value.toLowerCase())}
                            onBlur={() => {
                                if (productId && slugEditAllow) generateSlug(slug); // optional normalize + unique check
                            }}
                            required
                        />

                        {productId && slugEditAllow && (
                            <small className="text-warning d-block mt-2">
                                ⚠️ Note: Changing the slug will affect the existing Google indexed URL.
                                Old product links will be redirected to the new URL automatically.
                            </small>
                        )}

                        {productId && slugEditAllow && originalSlug && slug !== originalSlug && (
                            <small className="text-danger d-block mt-1">
                                Current slug changed from <b>{originalSlug}</b> → <b>{slug}</b>
                            </small>
                        )}
                        {errors.slug && <p className="text-danger">{errors.slug.message}</p>}
                    </div>


                    <div className="mb-3">
                        <label className="form-label">Product Tamil Name</label>
                        <input
                            {...register("productTamilName", { required: true })}
                            className="form-control"
                            type="text"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Category</label>
                        <Controller
                            name="categoryId"
                            control={control}
                            rules={{ required: "Category is required" }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    options={categories.map(cat => ({ value: cat.categoryID, label: cat.name }))}
                                    className="form-select"
                                    required
                                />
                            )}
                        />
                        {errors.categoryId && <p className="text-danger">{errors.categoryId.message}</p>}
                    </div>


                    <div className="mb-3">
                        <label className="form-label">Brand</label>
                        <Controller
                            name="brandId"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    options={brands.map(brand => ({ value: brand.id, label: brand.name }))}
                                    className="form-select"
                                    required
                                />
                            )}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Product Description</label>
                        <textarea
                            {...register("description", { required: true })}
                            className="form-control"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Related Products</label>
                        <Controller
                            name="related_products"
                            control={control}
                            defaultValue={[]}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    options={products}
                                    isMulti
                                    onChange={(selected) => {
                                        field.onChange(selected);
                                        // setSelectedRelatedProducts(selected); // update local state
                                    }}
                                    value={field.value}
                                    onInputChange={(newValue) => {
                                        handleSearchChange(newValue);
                                        return newValue;
                                    }}
                                    className="form-select"
                                />
                            )}
                        />

                    </div>

                    <Row className="mt-4">
                        <Col md="6">
                            <Form.Group className="mb-3 d-flex" controlId="formGroupProductAvailability">
                                <Form.Label className="col-4">Availability<span className="text-danger">*</span></Form.Label>
                                <Form.Check
                                    type="switch"
                                    id="availability-switch"
                                    checked={availability}
                                    onChange={(e) => setAvailability(e.target.checked)}
                                />
                            </Form.Group>
                        </Col>

                        <Col md="6">
                            <Form.Group className="mb-3 d-flex" controlId="formGroupProductSudexo">
                                <Form.Label className="col-4">Sudexo<span className="text-danger">*</span></Form.Label>
                                <Form.Check
                                    type="switch"
                                    id="sudexo-switch"
                                    checked={sudexo}
                                    onChange={(e) => setSudexo(e.target.checked)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>

                <Col md="6">
                    {/* New Fields Added */}
                    <Row className="mt-4">
                        <Col md="6">
                            <Form.Group className="mb-3 d-flex" controlId="formGroupProductWeighable">
                                <Form.Label className="col-4">Weighable<span className="text-danger">*</span></Form.Label>
                                <Form.Check
                                    type="switch"
                                    id="weighable-switch"
                                    checked={weighable}
                                    onChange={(e) => setWeighable(e.target.checked)}
                                />
                            </Form.Group>
                        </Col>

                        <Col md="6">
                            <Form.Group className="mb-3 d-flex" controlId="formGroupProductStockFlag">
                                <Form.Label className="col-4">Feartured Product<span className="text-danger">*</span></Form.Label>
                                <Form.Check
                                    type="switch"
                                    id="stockFlag-switch"
                                    checked={stockFlag}
                                    onChange={(e) => setStockFlag(e.target.checked)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>


                    <div className="mb-3">
                        <label className="form-label">Key Tags</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Press Enter to add key tags"
                            onKeyDown={handleKeyTagChange}
                        />
                        <div>
                            {keyTags.map((tag, index) => (
                                <span key={index} className="badge bg-primary me-2 mt-2">
                                    {tag}
                                    <button type="button" onClick={() => removeKeyTag(index)} className="btn-close ms-2"></button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* SEO Fields */}
                    <div className="mb-3">
                        <label className="form-label">SEO Title</label>
                        <input
                            {...register("seoTitle")}
                            value={seoTitle}
                            onChange={(e) => setSeoTitle(e.target.value)}
                            className="form-control"
                            placeholder="Enter SEO title here"
                        />

                    </div>

                    <div className="mb-3">
                        <label className="form-label">SEO Keywords</label>
                        <input
                            {...register("seoKeywords")}
                            value={seoKeywords}
                            onChange={(e) => setSeoKeywords(e.target.value)}
                            className="form-control"
                            placeholder="Enter SEO keywords here"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">SEO Description</label>
                        <textarea
                            {...register("seoDescription")}
                            value={seoDescription}
                            onChange={(e) => setSeoDescription(e.target.value)}
                            className="form-control"
                            placeholder="Enter SEO description here"
                            rows={5}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">HSN/SAC Code</label>
                        <input
                            {...register("hsn")}
                            value={hsn}
                            onChange={(e) => setHsnSacCode(e.target.value)}
                            className="form-control"
                            placeholder="Enter HSN/SAC Code"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Video URL</label>
                        <input
                            {...register("videoURL")}
                            value={videoURL}
                            onChange={(e) => setVideoURL(e.target.value)}
                            className="form-control"
                            placeholder="Enter Video URL"
                        />
                    </div>
                </Col>
            </Row>
            <button type="submit" className="btn btn-success mt-3 mb-3" disabled={loading}>
                {loading ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                    </>
                ) : (
                    "Save Product Details"
                )}
            </button>

            {(isProductSaved || newProductID) && (
                <ProductItemForm
                    productId={newProductID}
                    productItems={productItems}
                    onItemSave={async () => {
                        if (!newProductID) return;
                        try {
                            const response = await productAPI.getProductsData(newProductID);
                            const data = response?.data?.data || response?.data || response;
                            setProductItems(data?.items || []);
                        } catch (err) {
                            console.error("Failed to refresh product items", err);
                            toast.error("Failed to refresh product items");
                        }
                    }}
                    toast={toast}
                />
            )}


        </form>
    );
};

export default ProductForm;
